export interface LayoutEntity {
  id: string;
  name: string;
  type: string;
  color: string;
  x: number;
  y: number;
  size: number;
  width?: number;
  height?: number;
  cluster: string;
  host: string;
  data?: Record<string, unknown>;
}

export interface LayoutGroup {
  id: string;
  name: string;
  color: string;
  entities: string[];
}

const CLUSTER_PALETTE: Record<string, string> = {
  work: '#3b82f6',
  research: '#a855f7',
  shopping: '#f59e0b',
  social: '#ec4899',
  development: '#10b981',
  general: '#64748b',
};

const HOST_ACCENTS = [
  '#6366f1',
  '#14b8a6',
  '#f97316',
  '#e879f9',
  '#22d3ee',
  '#a3e635',
  '#fb7185',
  '#fbbf24',
];

const NODE_W = 210;
const NODE_H = 54;
const COL_GAP = 72;
const ROW_GAP = 18;
const HEADER_H = 40;
const PAD_TOP = 72;
const PAD_LEFT = 88;

export function clusterColor(cluster: string): string {
  const key = cluster.toLowerCase();
  return CLUSTER_PALETTE[key] ?? CLUSTER_PALETTE.general;
}

export function hostAccent(host: string): string {
  if (!host) return CLUSTER_PALETTE.general;
  let hash = 0;
  for (let i = 0; i < host.length; i++) {
    hash = (hash + host.charCodeAt(i) * 31) % HOST_ACCENTS.length;
  }
  return HOST_ACCENTS[hash]!;
}

export function truncateLabel(text: string, max = 42): string {
  const t = text.trim();
  if (t.length <= max) return t;
  return `${t.slice(0, max - 1)}…`;
}

function resolveCluster(
  entity: LayoutEntity,
  groupByEntity: Map<string, LayoutGroup>,
): string {
  const fromData = String(entity.data?.cluster ?? '').trim().toLowerCase();
  const host = resolveHost(entity);
  if (fromData && fromData !== 'general') return fromData;
  if (host) return `site:${host}`;
  const group = groupByEntity.get(entity.id);
  if (group?.name) {
    const gName = group.name.toLowerCase();
    if (gName !== 'general') return gName;
  }
  return 'general';
}

function resolveHost(entity: LayoutEntity): string {
  const fromData = String(entity.data?.host ?? '').trim();
  if (fromData) return fromData;
  return entity.host.trim();
}

/** Columnar cluster layout — readable for typical tab counts (≤ ~30). */
export function layoutRelationshipGraph(
  entities: LayoutEntity[],
  groups: LayoutGroup[],
): { entities: LayoutEntity[]; width: number; height: number } {
  if (entities.length === 0) {
    return { entities: [], width: 960, height: 640 };
  }

  const groupByEntity = new Map<string, LayoutGroup>();
  for (const g of groups) {
    for (const id of g.entities) groupByEntity.set(id, g);
  }

  const buckets = new Map<string, LayoutEntity[]>();
  for (const entity of entities) {
    const cluster = resolveCluster(entity, groupByEntity);
    const host = resolveHost(entity);
    const group = groupByEntity.get(entity.id);
    const baseColor = entity.color || group?.color;
    const color =
      cluster.startsWith('site:') || cluster === 'general'
        ? hostAccent(host)
        : baseColor || clusterColor(cluster);

    const normalized: LayoutEntity = {
      ...entity,
      cluster,
      host,
      color,
    };
    const list = buckets.get(cluster) ?? [];
    list.push(normalized);
    buckets.set(cluster, list);
  }

  const clusterOrder = [...buckets.keys()].sort((a, b) => {
    const aCount = buckets.get(a)?.length ?? 0;
    const bCount = buckets.get(b)?.length ?? 0;
    return bCount - aCount;
  });

  let colX = PAD_LEFT;
  let maxBottom = PAD_TOP;
  const positioned: LayoutEntity[] = [];

  for (const clusterKey of clusterOrder) {
    const items = buckets.get(clusterKey) ?? [];
    items.sort((a, b) => a.name.localeCompare(b.name));

    const displayName = clusterKey.startsWith('site:')
      ? clusterKey.slice(5)
      : clusterKey.charAt(0).toUpperCase() + clusterKey.slice(1);
    const accent = items[0]?.color ?? clusterColor(clusterKey);

    for (let i = 0; i < items.length; i++) {
      const y = PAD_TOP + HEADER_H + i * (NODE_H + ROW_GAP) + NODE_H / 2;
      positioned.push({
        ...items[i]!,
        x: colX + NODE_W / 2,
        y,
        size: 22,
        width: NODE_W,
        height: NODE_H,
        color: items[i]!.color || accent,
        cluster: displayName,
        host: items[i]!.host,
      });
      maxBottom = Math.max(maxBottom, y + NODE_H / 2 + 40);
    }

    // stash column meta on first node of cluster for header rendering
    if (items.length > 0) {
      const first = positioned.find((p) => p.id === items[0]!.id);
      if (first) {
        first.data = {
          ...(first.data ?? {}),
          _colX: colX,
          _colHeader: displayName,
          _colAccent: accent,
          _colCount: items.length,
        };
      }
    }

    colX += NODE_W + COL_GAP;
  }

  return {
    entities: positioned,
    width: Math.max(960, colX + PAD_LEFT),
    height: Math.max(520, maxBottom),
  };
}

export function computeGraphViewBox(
  entities: LayoutEntity[],
  width: number,
  height: number,
): string {
  if (entities.length === 0) return `0 0 ${width} ${height}`;
  return `0 0 ${width} ${height}`;
}

export function edgePath(
  x1: number,
  y1: number,
  x2: number,
  y2: number,
): string {
  const mx = (x1 + x2) / 2;
  const my = (y1 + y2) / 2;
  const dx = x2 - x1;
  const dy = y2 - y1;
  const curve = Math.min(80, Math.hypot(dx, dy) * 0.25);
  const cx = mx - dy * (curve / 200);
  const cy = my + dx * (curve / 200);
  return `M ${x1} ${y1} Q ${cx} ${cy} ${x2} ${y2}`;
}
